/**
 * Formatting Utilities
 * Functions for formatting numbers, dates, currencies, and other data
 */

import { BAG_CONVERSION_FACTORS, BAG_SIZE_OPTIONS } from '../constants';
import type { BagSize } from '../types/api';
import { logger } from './logger';

// ============================================================================
// Number Formatting
// ============================================================================

/**
 * Format number using Spanish locale
 * @param n - Number to format
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted number string
 */
export function formatNumberEs(n: number, decimals = 1): string {
  const safe = typeof n === 'number' && isFinite(n) ? n : 0;
  return safe.toLocaleString('es-ES', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Format number with custom locale
 * @param n - Number to format
 * @param locale - Locale string (default: 'es-ES')
 * @param decimals - Number of decimal places
 * @returns Formatted number string
 */
export function formatNumber(
  n: number,
  locale = 'es-ES',
  decimals = 1,
): string {
  const safe = typeof n === 'number' && isFinite(n) ? n : 0;
  return safe.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Format percentage with delta indicator
 * @param pct - Percentage value (can be null)
 * @returns Formatted delta string (without emoji)
 */
export function formatDelta(pct: number | null): string {
  if (pct === null || pct === undefined) return '— vs mes pasado';
  return `${formatNumberEs(Math.abs(pct), 1)}% vs mes pasado`;
}

/**
 * Format large numbers with K, M, B suffixes
 * @param num - Number to format
 * @param decimals - Number of decimal places
 * @returns Formatted string with suffix
 */
export function formatLargeNumber(num: number, decimals = 1): string {
  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  if (absNum >= 1e9) {
    return `${sign + formatNumberEs(absNum / 1e9, decimals)}B`;
  } else if (absNum >= 1e6) {
    return `${sign + formatNumberEs(absNum / 1e6, decimals)}M`;
  } else if (absNum >= 1e3) {
    return `${sign + formatNumberEs(absNum / 1e3, decimals)}K`;
  } else {
    return sign + formatNumberEs(absNum, decimals);
  }
}

// ============================================================================
// Currency Formatting
// ============================================================================

/**
 * Format currency amount
 * @param amount - Amount to format
 * @param currency - Currency code (default: 'EUR')
 * @param locale - Locale string (default: 'es-ES')
 * @returns Formatted currency string
 * @deprecated Use useCurrency hook instead for profile-based currency formatting
 */
export function formatCurrency(
  amount: number,
  currency = 'EUR',
  locale = 'es-ES',
): string {
  const safe = typeof amount === 'number' && isFinite(amount) ? amount : 0;
  return safe.toLocaleString(locale, {
    style: 'currency',
    currency,
  });
}

/**
 * Format currency with custom symbol
 * @param amount - Amount to format
 * @param symbol - Currency symbol (e.g., '€', '$')
 * @param decimals - Number of decimal places
 * @returns Formatted currency string
 * @deprecated Use useCurrency hook instead for profile-based currency formatting
 */
export function formatCurrencyWithSymbol(
  amount: number,
  symbol = '€',
  decimals = 2,
): string {
  const safe = typeof amount === 'number' && isFinite(amount) ? amount : 0;
  return `${formatNumberEs(safe, decimals)} ${symbol}`;
}

// ============================================================================
// Date Formatting
// ============================================================================

/**
 * Format date string to Spanish locale
 * @param dateString - ISO date string
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export function formatDate(
  dateString: string,
  options: Intl.DateTimeFormatOptions = {},
): string {
  try {
    const date = new Date(dateString);
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    return date.toLocaleDateString('es-ES', { ...defaultOptions, ...options });
  } catch {
    return dateString;
  }
}

/**
 * Format date to short format (DD/MM/YYYY)
 * @param dateString - ISO date string
 * @returns Short date string
 */
export function formatDateShort(dateString: string): string {
  return formatDate(dateString, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/**
 * Format date to relative time (e.g., "hace 2 días")
 * @param dateString - ISO date string
 * @returns Relative time string
 */
export function formatRelativeTime(dateString: string): string {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffDays > 0) {
      return `hace ${diffDays} día${diffDays > 1 ? 's' : ''}`;
    } else if (diffHours > 0) {
      return `hace ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
    } else if (diffMinutes > 0) {
      return `hace ${diffMinutes} minuto${diffMinutes > 1 ? 's' : ''}`;
    } else {
      return 'hace unos momentos';
    }
  } catch {
    return dateString;
  }
}

/**
 * Format datetime to Spanish locale
 * @param dateString - ISO datetime string
 * @returns Formatted datetime string
 */
export function formatDateTime(dateString: string): string {
  return formatDate(dateString, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format time only
 * @param dateString - ISO datetime string
 * @returns Formatted time string
 */
export function formatTime(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
}

/**
 * Safe date formatting for HTML date inputs (YYYY-MM-DD format)
 * Guarantees a valid string return type for TypeScript strict mode
 * @param date - Date object or timestamp
 * @returns Date string in YYYY-MM-DD format
 */
export function formatDateForInput(date: Date | number): string {
  try {
    const dateObj = typeof date === 'number' ? new Date(date) : date;
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    // Fallback to current date if parsing fails
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}

// ============================================================================
// ISO Date String Utilities
// ============================================================================

/**
 * Type guard to check if a value is a valid date string
 * @param value - Value to check
 * @returns True if value is a non-empty string that can be parsed as a date
 */
export function isValidDateString(value: unknown): value is string {
  if (typeof value !== 'string' || !value.trim()) {
    return false;
  }

  try {
    const date = new Date(value);
    return !isNaN(date.getTime());
  } catch {
    return false;
  }
}

/**
 * Convert ISO date string to YYYY-MM-DD format for HTML date inputs
 * Handles null/undefined values and various ISO formats safely
 * @param isoDateString - ISO date string like "2024-01-15T00:00:00" or null/undefined
 * @returns YYYY-MM-DD format string like "2024-01-15" or empty string if invalid
 */
export function formatIsoDateForInput(
  isoDateString: string | undefined | null,
): string {
  // Early return for null/undefined/empty values
  if (!isoDateString) {
    logger.debug('Received null/undefined/empty date string', {
      component: 'formatIsoDateForInput',
      input: isoDateString,
    });
    return '';
  }

  // Type guard validation
  if (!isValidDateString(isoDateString)) {
    logger.warn('Invalid date string provided', undefined, {
      component: 'formatIsoDateForInput',
      input: isoDateString,
    });
    return '';
  }

  try {
    // Handle both ISO format (with T separator) and already formatted dates (YYYY-MM-DD)
    if (isoDateString.includes('T')) {
      // ISO format: "2024-01-15T00:00:00" -> "2024-01-15"
      const dateOnly = isoDateString.split('T')[0];
      if (!dateOnly) {
        throw new Error('Failed to extract date from ISO string');
      }
      logger.debug('Extracted date from ISO format', {
        component: 'formatIsoDateForInput',
        input: isoDateString,
        output: dateOnly,
      });
      return dateOnly;
    } else {
      // Already in YYYY-MM-DD format or other parseable format
      const date = new Date(isoDateString);
      if (isNaN(date.getTime())) {
        throw new Error(`Invalid date: ${isoDateString}`);
      }

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const formatted = `${year}-${month}-${day}`;

      logger.debug('Formatted date string', {
        component: 'formatIsoDateForInput',
        input: isoDateString,
        output: formatted,
      });
      return formatted;
    }
  } catch (error) {
    logger.error('Error formatting date string', error as Error, {
      component: 'formatIsoDateForInput',
      input: isoDateString,
    });
    return '';
  }
}

// ============================================================================
// Service Type & Unit Formatting
// ============================================================================

/**
 * Format service type with emoji
 * @param type - Service type string
 * @returns Formatted service type with emoji
 */
export function formatServiceType(type: string): string {
  const types: Record<string, string> = {
    electricidad: 'Electricidad',
    agua: 'Agua',
    gas: 'Gas',
    'residuos-reciclables': 'Residuos Reciclables',
    'residuos-organicos': 'Residuos Orgánicos',
    'residuos-generales': 'Residuos Generales',
    'covers-volumes': 'Cubiertos',
    'supplier-operations': 'Cubiertos',
    'compliance-document': 'Cumplimiento',
  };
  return types[type] || type;
}

/**
 * Get unit for service type
 * @param serviceType - Service type
 * @returns Unit string
 */
const WATER_M3_TO_LITERS = 1000;

export function convertWaterToLiters(
  value: number | null | undefined,
): number | null {
  if (value === null || value === undefined) {
    return null;
  }

  return value * WATER_M3_TO_LITERS;
}

export const WATER_UNIT = {
  TOTAL: 'L',
  PER_DISH: 'L/plato',
} as const;

export function getUnit(serviceType: string): string {
  const units: Record<string, string> = {
    electricidad: 'kWh',
    agua: 'm³',
    gas: 'm³',
    'residuos-reciclables': 'kg',
    'residuos-organicos': 'kg',
    'residuos-generales': 'kg',
  };
  return units[serviceType] || '';
}

/**
 * Get available units for a service type
 * @param serviceType - Service type
 * @returns Array of available units
 */
export function getAvailableUnits(serviceType: string): string[] {
  const unitOptions: Record<string, string[]> = {
    electricidad: ['kWh'],
    agua: [WATER_UNIT.TOTAL],
    gas: ['m³'],
    'residuos-reciclables': ['kg', 'bolsas'],
    'residuos-organicos': ['kg', 'bolsas'],
    'residuos-generales': ['kg', 'bolsas'],
  };
  return unitOptions[serviceType] || [];
}

export function getBagConversionFactor(size: BagSize): number {
  return BAG_CONVERSION_FACTORS[size] ?? BAG_CONVERSION_FACTORS.medium;
}

export function getBagSizeLabel(size: BagSize): string {
  return (
    BAG_SIZE_OPTIONS.find((option) => option.value === size)?.label ?? size
  );
}

export function formatBagQuantity(count: number, size: BagSize): string {
  const baseLabel = count === 1 ? 'bolsa' : 'bolsas';
  return `${count} ${baseLabel} (${getBagSizeLabel(size)})`;
}

export function calculateBagKilograms(bags: number, size: BagSize): number {
  if (!Number.isFinite(bags)) {
    return 0;
  }
  return bags * getBagConversionFactor(size);
}

/**
 * Check if a service type is a waste/residuos type
 * @param serviceType - Service type to check
 * @returns True if it's a waste service type
 */
export function isWasteServiceType(serviceType: string): boolean {
  return serviceType.startsWith('residuos-');
}

/**
 * Format consumption with unit
 * @param consumption - Consumption value
 * @param serviceType - Service type
 * @returns Formatted consumption with unit
 */
export function formatConsumption(
  consumption: number,
  serviceType: string,
): string {
  const unit = getUnit(serviceType);
  return `${formatNumberEs(consumption)} ${unit}`;
}

// ============================================================================
// File Size Formatting
// ============================================================================

/**
 * Format file size in bytes to human readable format
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
}

// ============================================================================
// Percentage & Progress Formatting
// ============================================================================

/**
 * Format percentage
 * @param value - Percentage value (0-100)
 * @param decimals - Number of decimal places
 * @returns Formatted percentage string
 */
export function formatPercentage(value: number, decimals = 1): string {
  const safe = typeof value === 'number' && isFinite(value) ? value : 0;
  return `${formatNumberEs(safe, decimals)}%`;
}

/**
 * Format progress with bar representation
 * @param current - Current value
 * @param total - Total value
 * @param showPercentage - Whether to show percentage
 * @returns Formatted progress string
 */
export function formatProgress(
  current: number,
  total: number,
  showPercentage = true,
): string {
  const percentage = total > 0 ? (current / total) * 100 : 0;
  const progressText = `${formatNumberEs(current)} / ${formatNumberEs(total)}`;

  if (showPercentage) {
    return `${progressText} (${formatPercentage(percentage)})`;
  }
  return progressText;
}

// ============================================================================
// Address & Location Formatting
// ============================================================================

/**
 * Format full address
 * @param parts - Address parts
 * @returns Formatted address string
 */
export function formatAddress(parts: {
  street?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
}): string {
  const addressParts = [
    parts.street,
    parts.city,
    parts.state,
    parts.country,
    parts.postalCode,
  ].filter(Boolean);

  return addressParts.join(', ');
}

/**
 * Format location (city, country)
 * @param city - City name
 * @param country - Country name
 * @returns Formatted location string
 */
export function formatLocation(city?: string, country?: string): string {
  const parts = [city, country].filter(Boolean);
  return parts.join(', ');
}

// ============================================================================
// Text Formatting Utilities
// ============================================================================

/**
 * Truncate text to specified length
 * @param text - Text to truncate
 * @param maxLength - Maximum length
 * @param suffix - Suffix to add (default: '...')
 * @returns Truncated text
 */
export function truncateText(
  text: string,
  maxLength: number,
  suffix = '...',
): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - suffix.length) + suffix;
}

/**
 * Capitalize first letter
 * @param text - Text to capitalize
 * @returns Capitalized text
 */
export function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

/**
 * Convert to title case
 * @param text - Text to convert
 * @returns Title case text
 */
export function toTitleCase(text: string): string {
  return text.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(),
  );
}

// ============================================================================
// Validation Formatting
// ============================================================================

/**
 * Format validation error message
 * @param field - Field name
 * @param error - Error message
 * @returns Formatted error message
 */
export function formatValidationError(field: string, error: string): string {
  return `${capitalize(field)}: ${error}`;
}

/**
 * Format multiple validation errors
 * @param errors - Array of error objects
 * @returns Formatted error messages
 */
export function formatValidationErrors(
  errors: Array<{ field: string; message: string }>,
): string[] {
  return errors.map((error) =>
    formatValidationError(error.field, error.message),
  );
}

// ============================================================================
// Confidence Formatting
// ============================================================================

/**
 * Convert confidence value to percentage
 * Handles both decimal (0.98) and percentage (98) formats
 * @param confidence - Confidence value (0.98 or 98)
 * @returns Percentage value (98)
 */
export function formatConfidence(confidence: number): number {
  return confidence <= 1 ? confidence * 100 : confidence;
}
